import { useEffect, useRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface ErrorSummaryProps {
  messages: string[];
}

/**
 * Red error banner that lists server-side failure messages. Renders nothing
 * when ``messages`` is empty, and scrolls itself into view whenever it becomes
 * non-empty so the user always sees why a submission failed.
 */
export function ErrorSummary({ messages }: ErrorSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messages.length > 0) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  if (messages.length === 0) return null;

  return (
    <div
      ref={ref}
      role="alert"
      className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20"
    >
      <div className="flex">
        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-400 dark:text-red-400" aria-hidden="true" />
        <div className="ml-3">
          <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
            {messages.length === 1
              ? 'There was a problem'
              : `There were ${messages.length} problems`}
          </h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700 dark:text-red-300">
            {messages.map((message, i) => (
              <li key={i}>{message}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
